import { Box, Grid, GridItem, Heading, Tabs, VStack } from "@chakra-ui/react"
import { useMemo, useState } from "react"

import { BatchUploadModal } from "../BatchUploadModal"
import { QuizBuilderHeader } from "../QuizBuilderHeader"
import { QuizPreview } from "../QuizPreview"
import { QuizQuestionBank } from "../QuizQuestionBank"
import { QuizSettingsForm } from "../QuizSettingsForm"
import { SectionedQuestionsPanel } from "../SectionedQuestionsPanel"
import type {
  QuizBuilderMode,
  QuizSection,
  SelectedQuestion,
} from "../quiz-builder-types"

import { QuestionPreviewModal } from "@/components/topics/QuestionPreviewModal"
import { getQuizPublishStatus } from "@/utils/quiz-status"
import { QuizBuilderProvider, useQuizBuilder } from "./QuizBuilderContext"

export interface QuizBuilderProps {
  mode: QuizBuilderMode
  quizId?: string
}

function QuizBuilderContent({ mode, quizId }: QuizBuilderProps) {
  const [previewQuestionId, setPreviewQuestionId] = useState<string | null>(
    null,
  )
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const {
    // State
    settings,
    selectedPrograms,
    searchValue,
    selectedSubjects,
    selectedTopics,
    sections,
    unsectionedQuestions,
    activeTab,
    isBatchUploadOpen,

    // Computed
    availableQuestions,
    isQuestionSelected,
    isSaving,
    hasUnsavedChanges,

    // Data
    loadingQuestions,
    quizInitializationData,

    // Actions
    setSearchValue,
    setSelectedSubjects,
    setSelectedTopics,
    setSelectedPrograms,
    setActiveTab,
    setIsBatchUploadOpen,

    handleAddQuestion,
    handleRemoveQuestion,
    handleClearSectionQuestions,
    handleAddSection,
    handleRemoveSection,
    handleUpdateSection,
    handleToggleSection,
    handleDragEnd,
    handleSettingChange,
    buildTemplateSettingsPayload,
    applyTemplateSettings,
    handleSaveQuiz,
    handleBatchUploadSuccess,
  } = useQuizBuilder()

  const handlePreviewQuestion = (questionId: string) => {
    setPreviewQuestionId(questionId)
    setIsPreviewOpen(true)
  }

  const handleClosePreview = () => {
    setIsPreviewOpen(false)
    setPreviewQuestionId(null)
  }

  const publishStatus = getQuizPublishStatus({
    publishDate: settings.publish_date,
    cutoffDate: settings.cutoff_date,
  })

  const savedPreviewState = useMemo(() => {
    if (mode !== "edit" || !quizInitializationData) {
      return null
    }

    const orderedLinks = [
      ...(quizInitializationData.quiz.questions ?? []),
    ].sort((a, b) => a.order - b.order)
    const questionById = new Map(
      quizInitializationData.questions.map((question) => [
        question.id,
        question,
      ]),
    )
    const sectionMap = new Map<string, QuizSection>()
    const savedSections = (quizInitializationData.sections ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)

    for (const section of savedSections) {
      sectionMap.set(section.id, {
        id: section.id,
        name: section.name,
        description: section.description ?? null,
        order: section.order,
        questions: [],
        isCollapsed: false,
      })
    }

    const savedUnsectionedQuestions: SelectedQuestion[] = []

    for (const link of orderedLinks) {
      const question = questionById.get(link.question_id)
      if (!question) {
        continue
      }

      if (link.section_id) {
        const section = sectionMap.get(link.section_id)
        if (section) {
          section.questions.push(question)
          continue
        }
      }

      savedUnsectionedQuestions.push(question)
    }

    return {
      quizName: quizInitializationData.quiz.name,
      sections: Array.from(sectionMap.values()),
      unsectionedQuestions: savedUnsectionedQuestions,
      showRationaleMode:
        quizInitializationData.quiz.show_rationale_mode ?? "after_all",
    }
  }, [mode, quizInitializationData])

  const previewState = savedPreviewState ?? {
    quizName: settings.name,
    sections,
    unsectionedQuestions,
    showRationaleMode: settings.show_rationale_mode ?? "after_all",
  }

  return (
    <Box w="100%" py={10} px={{ base: 0, xl: 4 }} data-testid="quiz-builder">
      <QuizBuilderHeader
        mode={mode}
        onSave={handleSaveQuiz}
        isSaving={isSaving}
        publishStatus={publishStatus}
        onBatchUpload={
          mode === "edit" && quizId
            ? () => setIsBatchUploadOpen(true)
            : undefined
        }
      />

      {mode === "edit" && quizId && (
        <BatchUploadModal
          isOpen={isBatchUploadOpen}
          onClose={() => setIsBatchUploadOpen(false)}
          quizId={quizId}
          sections={quizInitializationData?.sections || []}
          onUploadSuccess={handleBatchUploadSuccess}
        />
      )}

      <Heading size="lg" mb={4}>
        {mode === "edit" ? "Edit Quiz" : "Create New Quiz"}
      </Heading>

      <Tabs.Root
        value={activeTab}
        onValueChange={(event) =>
          setActiveTab((event.value as "builder" | "preview") ?? "builder")
        }
        colorPalette="blue"
      >
        <Tabs.List>
          <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
          <Tabs.Trigger value="builder">Builder</Tabs.Trigger>
          <Tabs.Trigger value="preview">Preview</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="settings" mt={4}>
          <QuizSettingsForm
            settings={settings}
            onSettingChange={handleSettingChange}
            selectedPrograms={selectedPrograms}
            onProgramsChange={setSelectedPrograms}
            buildTemplateSettingsPayload={buildTemplateSettingsPayload}
            onApplyTemplateSettings={applyTemplateSettings}
          />
        </Tabs.Content>

        <Tabs.Content value="builder" mt={4}>
          <Box px={0}>
            <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap={6}>
              <GridItem>
                <SectionedQuestionsPanel
                  sections={sections}
                  unsectionedQuestions={unsectionedQuestions}
                  onAddSection={handleAddSection}
                  onRemoveSection={handleRemoveSection}
                  onUpdateSection={handleUpdateSection}
                  onToggleSection={handleToggleSection}
                  onRemoveQuestion={handleRemoveQuestion}
                  onClearSectionQuestions={handleClearSectionQuestions}
                  onPreviewQuestion={handlePreviewQuestion}
                  onDragEnd={handleDragEnd}
                />
              </GridItem>
              <GridItem>
                <VStack gap={6} align="stretch">
                  <QuizQuestionBank
                    loading={loadingQuestions}
                    availableQuestions={availableQuestions}
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    selectedSubjects={selectedSubjects}
                    onSubjectsChange={setSelectedSubjects}
                    selectedTopics={selectedTopics}
                    onTopicsChange={setSelectedTopics}
                    isQuestionSelected={isQuestionSelected}
                    onAddQuestion={handleAddQuestion}
                    onPreviewQuestion={handlePreviewQuestion}
                  />
                </VStack>
              </GridItem>
            </Grid>
          </Box>
        </Tabs.Content>

        <Tabs.Content value="preview" mt={4}>
          <Box px={0}>
            <QuizPreview
              quizName={previewState.quizName}
              sections={previewState.sections}
              unsectionedQuestions={previewState.unsectionedQuestions}
              showRationaleMode={previewState.showRationaleMode}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          </Box>
        </Tabs.Content>
      </Tabs.Root>

      <QuestionPreviewModal
        questionId={previewQuestionId}
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
      />
    </Box>
  )
}

export default function QuizBuilder({ mode, quizId }: QuizBuilderProps) {
  return (
    <QuizBuilderProvider mode={mode} quizId={quizId}>
      <QuizBuilderContent mode={mode} quizId={quizId} />
    </QuizBuilderProvider>
  )
}

// Re-export types
export type { SelectedQuestion } from "../quiz-builder-types"
